import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import MainLayout from '../../components/MainLayout';
import { Card, Form, Input, Select, Button, Table, message, Spin, Row, Col, Space, Tag, Typography, Statistic, Divider, Progress, Tooltip, Empty } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ApiConfig, driversListUrl } from '../../services/ApiConfigs';
import { ApiServices } from '../../services/apiServices';
import { BellOutlined, SendOutlined, ReloadOutlined, HistoryOutlined, RetweetOutlined, DeleteOutlined } from '@ant-design/icons';

const CustomNotifications = () => {
    const userDetail = useSelector((state) => state.UserData.userData);
    const storeId = userDetail?.user?.storeId ?? userDetail?.storeId;
    const userDbRole = userDetail?.user?.role; // super_admin | store_owner | staff
    const isStaff = userDbRole === 'staff';
    const perms = userDetail?.permissions || {};
    const canRead = !isStaff || !!perms?.['notifications.read'];
    const canWrite = !isStaff || !!perms?.['notifications.write'];
    const canDelete = !isStaff || !!perms?.['notifications.delete'];

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [total, setTotal] = useState(0);
    const [drivers, setDrivers] = useState([]);
    const [users, setUsers] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [page, setPage] = useState(1);
    const limit = 10;
    const [historyQuery, setHistoryQuery] = useState('');

    const { Title, Text } = Typography;

    const recipientTypeOptions = [
        { value: 'driver', label: 'Drivers' },
        { value: 'both', label: 'Drivers & Users' },
    ];

    const targetTypeOptions = [
        { value: 'single', label: 'Single recipient' },
        { value: 'selected', label: 'Selected recipients' },
        { value: 'warehouse', label: 'By Warehouse' },
        { value: 'all', label: 'All (this store)' },
    ];

    useEffect(() => {
        loadData();
    }, [storeId]);

    useEffect(() => {
        if (canRead) loadNotifications();
    }, [page]);

    const loadData = async () => {
        try {
            const [driversRes, usersRes, warehousesRes] = await Promise.all([
                ApiServices('get', driversListUrl(storeId)),
                ApiServices('get', ApiConfig.USERS),
                ApiServices('get', ApiConfig.WAREHOUSE),
            ]);
            let allDrivers = Array.isArray(driversRes?.data) ? driversRes.data : [];
            let allUsers = Array.isArray(usersRes?.data) ? usersRes.data : [];
            let allWarehouses = Array.isArray(warehousesRes?.data) ? warehousesRes.data : [];
            if (storeId) {
                allUsers = allUsers.filter((u) => Number(u.storeId) === Number(storeId));
                allWarehouses = allWarehouses.filter((w) => Number(w.storeId) === Number(storeId));
            }
            setDrivers(allDrivers);
            setUsers(allUsers);
            setWarehouses(allWarehouses);
        } catch (e) {
            message.error('Failed to load drivers/warehouses');
        }
    };

    const loadNotifications = async () => {
        setListLoading(true);
        try {
            const res = await ApiServices('get', `${ApiConfig.CUSTOM_NOTIFICATIONS}?page=${page}&limit=${limit}`);
            if (res?.status && res?.data) {
                setNotifications(res.data.items || []);
                setTotal(res.data.total || 0);
            } else {
                setNotifications([]);
            }
        } catch (e) {
            message.error('Failed to load notifications');
        } finally {
            setListLoading(false);
        }
    };

    const onFinish = async (values) => {
        if (!canWrite) {
            message.error("You don't have permission to send notifications.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                title: values.title,
                body: values.body,
                recipient_type: values.recipient_type || 'driver',
                target_type: values.target_type,
            };

            if (values.target_type === 'single' || values.target_type === 'selected') {
                const rt = values.recipient_type || 'driver';
                if (rt === 'driver' || rt === 'both') {
                    const ids = Array.isArray(values.target_driver_ids) ? values.target_driver_ids : (values.target_driver_ids ? [values.target_driver_ids] : []);
                    if (ids.length) payload.target_driver_ids = ids;
                }
                if (rt === 'user' || rt === 'both') {
                    const ids = Array.isArray(values.target_user_ids) ? values.target_user_ids : (values.target_user_ids ? [values.target_user_ids] : []);
                    if (ids.length) payload.target_user_ids = ids;
                }
            } else if (values.target_type === 'warehouse' && values.target_warehouse_id) {
                payload.target_warehouse_id = values.target_warehouse_id;
            }
            if (storeId) payload.store_id = storeId;

            const res = await ApiServices('post', ApiConfig.CUSTOM_NOTIFICATIONS, payload);
            if (res?.status) {
                message.success(`Notification sent to ${res.data?.totalRecipients || 0} recipient(s)`);
                form.resetFields();
                if (canRead) loadNotifications();
            } else {
                message.error(res?.error || 'Failed to send notification');
            }
        } catch (e) {
            message.error(e?.message || 'Failed to send notification');
        } finally {
            setLoading(false);
        }
    };

    const resendFromRow = async (row) => {
        if (!canWrite) {
            message.error("You don't have permission to send notifications.");
            return;
        }
        const payload = {
            title: row?.title,
            body: row?.body,
            recipient_type: row?.recipient_type || 'driver',
            target_type: row?.target_type,
        };
        if (!payload.title || !payload.body || !payload.target_type) {
            message.error('This notification cannot be resent (missing data).');
            return;
        }
        // DB stores these fields with the same names
        if (payload.target_type === 'single' || payload.target_type === 'selected') {
            if (row?.target_driver_ids) payload.target_driver_ids = row.target_driver_ids;
            if (row?.target_user_ids) payload.target_user_ids = row.target_user_ids;
        } else if (payload.target_type === 'warehouse' && row?.target_warehouse_id) {
            payload.target_warehouse_id = row.target_warehouse_id;
        }
        // Store scope (super admin uses store_id, store users are auto-scoped by backend)
        if (row?.target_store_id) payload.store_id = row.target_store_id;

        setLoading(true);
        try {
            const res = await ApiServices('post', ApiConfig.CUSTOM_NOTIFICATIONS, payload);
            if (res?.status) {
                message.success(`Resent to ${res.data?.totalRecipients || 0} recipient(s)`);
                if (canRead) loadNotifications();
            } else {
                message.error(res?.error || 'Failed to resend');
            }
        } catch (e) {
            message.error(e?.message || 'Failed to resend');
        } finally {
            setLoading(false);
        }
    };

    const deleteRow = async (row) => {
        if (!canDelete) {
            message.error("You don't have permission to delete notifications.");
            return;
        }
        if (!row?.id) return;
        setListLoading(true);
        try {
            const res = await ApiServices('delete', `${ApiConfig.CUSTOM_NOTIFICATIONS}/${row.id}`);
            if (res?.status) {
                message.success('Deleted');
                if (canRead) loadNotifications();
            } else {
                message.error(res?.error || 'Failed to delete');
            }
        } catch (e) {
            message.error(e?.message || 'Failed to delete');
        } finally {
            setListLoading(false);
        }
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (t, r) => (
                <div style={{ minWidth: 220 }}>
                    <Text strong style={{ color: '#0f172a' }}>{t || '-'}</Text>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {r?.body ? String(r.body).slice(0, 80) : ''}
                        {r?.body && String(r.body).length > 80 ? '…' : ''}
                    </div>
                </div>
            ),
        },
        {
            title: 'Target',
            key: 'target',
            render: (_, r) => {
                const t = r.target_type;
                if (t === 'single') return <Tag color="blue">Single</Tag>;
                if (t === 'selected') return <Tag color="geekblue">Selected</Tag>;
                if (t === 'warehouse' && r.target_warehouse_id) return <Tag color="gold">{`Warehouse #${r.target_warehouse_id}`}</Tag>;
                return <Tag color="green">{t === 'all' ? 'All (store)' : String(t || '-')}</Tag>;
            },
            width: 160,
        },
        {
            title: 'Recipients',
            key: 'recipients',
            render: (_, r) => {
                const dc = r.deliveryCount || {};
                const sent = Number(dc.sent || 0);
                const totalN = Number(dc.total || 0);
                const pct = totalN > 0 ? Math.round((sent / totalN) * 100) : 0;
                return (
                    <div style={{ minWidth: 180 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#334155' }}>
                            <span>{sent} / {totalN} sent</span>
                            <span style={{ color: '#64748b' }}>{pct}%</span>
                        </div>
                        <Progress percent={pct} showInfo={false} size="small" />
                    </div>
                );
            },
        },
        {
            title: 'Sent at',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (v) => v ? new Date(v).toLocaleString() : '-',
            width: 200,
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, r) => (
                <Space size={2}>
                    <Tooltip title={canWrite ? 'Resend' : 'No permission'}>
                        <Button
                            type="text"
                            size="small"
                            icon={<RetweetOutlined style={{ color: canWrite ? '#1677ff' : '#bfbfbf' }} />}
                            onClick={() => resendFromRow(r)}
                            disabled={!canWrite}
                            style={{ padding: 4 }}
                        />
                    </Tooltip>
                    <Tooltip title={canDelete ? 'Delete' : 'No permission'}>
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined style={{ color: canDelete ? '#ff4d4f' : '#bfbfbf' }} />}
                            onClick={() => deleteRow(r)}
                            disabled={!canDelete}
                            style={{ padding: 4 }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const showDriverSelect = (rt, tt) =>
        (rt === 'driver' || rt === 'both') && (tt === 'single' || tt === 'selected');
    const showUserSelect = (rt, tt) =>
        (rt === 'user' || rt === 'both') && (tt === 'single' || tt === 'selected');
    const showWarehouseSelect = (tt) => tt === 'warehouse';

    const filteredNotifications = historyQuery.trim()
        ? notifications.filter((n) => {
            const q = historyQuery.trim().toLowerCase();
            const hay = [n?.title, n?.body, n?.target_type, n?.recipient_type].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        })
        : notifications;

    return (
        <MainLayout>
            <div style={{ padding: '0 16px' }}>
                <div style={{
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 12,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                }}>
                    <Space size={10}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'linear-gradient(135deg,#1677ff,#0050b3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <BellOutlined style={{ color: '#fff', fontSize: 18 }} />
                        </div>
                        <div>
                            <Title level={4} style={{ margin: 0 }}>Notifications</Title>
                            <Text type="secondary" style={{ fontSize: 12 }}>Send custom notifications to drivers/users and track delivery.</Text>
                        </div>
                    </Space>
                    <Space>
                        <Tooltip title="Reload recipients + warehouses">
                            <Button icon={<ReloadOutlined />} onClick={loadData}>Reload data</Button>
                        </Tooltip>
                        <Tooltip title="Reload history">
                            <Button icon={<HistoryOutlined />} onClick={loadNotifications} loading={listLoading} disabled={!canRead}>Reload history</Button>
                        </Tooltip>
                    </Space>
                </div>

                <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                        <Card style={{ borderRadius: 14 }}>
                            <Statistic title="Drivers" value={drivers.length} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card style={{ borderRadius: 14 }}>
                            <Statistic title="Users" value={users.length} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card style={{ borderRadius: 14 }}>
                            <Statistic title="Warehouses" value={warehouses.length} />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                    <Col xs={24} lg={10}>
                        <Card
                            style={{ borderRadius: 14 }}
                            title={<Space><SendOutlined style={{ color: '#1677ff' }} /><span>Send Notification</span></Space>}
                        >
                            {!canWrite && (
                                <div style={{
                                    padding: 10,
                                    borderRadius: 12,
                                    background: '#fff7e6',
                                    border: '1px solid #ffe7ba',
                                    color: '#ad4e00',
                                    marginBottom: 12,
                                    fontSize: 12,
                                }}>
                                    You have <b>view-only</b> access for Notifications. Ask admin to enable <b>notifications.write</b>.
                                </div>
                            )}
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                initialValues={{ recipient_type: 'driver', target_type: 'single' }}
                            >
                                <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                                    <Input placeholder="Notification title" disabled={!canWrite} />
                                </Form.Item>
                                <Form.Item name="body" label="Message" rules={[{ required: true }]}>
                                    <TextArea rows={4} placeholder="Write a short message…" disabled={!canWrite} />
                                </Form.Item>

                                <Row gutter={10}>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="recipient_type" label="Recipient type">
                                            <Select options={recipientTypeOptions} disabled={!canWrite} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="target_type" label="Target">
                                            <Select options={targetTypeOptions} disabled={!canWrite} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, curr) =>
                                        prev.recipient_type !== curr.recipient_type ||
                                        prev.target_type !== curr.target_type
                                    }
                                >
                                    {({ getFieldValue }) => {
                                        const rt = getFieldValue('recipient_type');
                                        const tt = getFieldValue('target_type');
                                        return (
                                            <>
                                                {showDriverSelect(rt, tt) && (
                                                    <Form.Item
                                                        name="target_driver_ids"
                                                        label={tt === 'single' ? 'Driver' : 'Drivers'}
                                                        rules={[
                                                            {
                                                                required: tt === 'single' || tt === 'selected',
                                                                message: 'Select at least one driver',
                                                            },
                                                        ]}
                                                    >
                                                        <Select
                                                            mode={tt === 'selected' ? 'multiple' : undefined}
                                                            placeholder="Choose driver(s)"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            disabled={!canWrite}
                                                            options={drivers.map((d) => ({
                                                                value: d.id,
                                                                label: `${d.firstName || ''} ${d.lastName || ''} (${d.email})`,
                                                            }))}
                                                        />
                                                    </Form.Item>
                                                )}
                                                {showUserSelect(rt, tt) && (
                                                    <Form.Item
                                                        name="target_user_ids"
                                                        label={tt === 'single' ? 'User' : 'Users'}
                                                        rules={[
                                                            {
                                                                required: tt === 'single' || tt === 'selected',
                                                                message: 'Select at least one user',
                                                            },
                                                        ]}
                                                    >
                                                        <Select
                                                            mode={tt === 'selected' ? 'multiple' : undefined}
                                                            placeholder="Choose user(s)"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            disabled={!canWrite}
                                                            options={users.map((u) => ({
                                                                value: u.id,
                                                                label: `${u.firstname || ''} ${u.lastname || ''} (${u.email})`,
                                                            }))}
                                                        />
                                                    </Form.Item>
                                                )}
                                                {showWarehouseSelect(tt) && (
                                                    <Form.Item
                                                        name="target_warehouse_id"
                                                        label="Warehouse"
                                                        rules={[{ required: true, message: 'Select a warehouse' }]}
                                                    >
                                                        <Select
                                                            placeholder="Select warehouse"
                                                            disabled={!canWrite}
                                                            options={warehouses.map((w) => ({
                                                                value: w.id,
                                                                label: w.name || `Warehouse #${w.id}`,
                                                            }))}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </>
                                        );
                                    }}
                                </Form.Item>

                                <Divider style={{ margin: '12px 0' }} />
                                <Space wrap>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />} disabled={!canWrite}>
                                        Send
                                    </Button>
                                    <Button onClick={() => form.resetFields()} disabled={loading || !canWrite}>
                                        Reset
                                    </Button>
                                </Space>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} lg={14}>
                        <Card
                            style={{ borderRadius: 14 }}
                            title={<Space><HistoryOutlined style={{ color: '#1677ff' }} /><span>History</span></Space>}
                            extra={
                                <Input
                                    value={historyQuery}
                                    onChange={(e) => setHistoryQuery(e.target.value)}
                                    allowClear
                                    placeholder="Search history…"
                                    style={{ width: 260 }}
                                    disabled={!canRead}
                                />
                            }
                        >
                            <Spin spinning={listLoading}>
                                {!canRead ? (
                                    <Empty description="You don't have permission to view notification history." />
                                ) : filteredNotifications.length === 0 ? (
                                    <Empty description={historyQuery ? "No matches" : "No notifications yet"} />
                                ) : (
                                    <Table
                                        rowKey="id"
                                        columns={columns}
                                        dataSource={filteredNotifications}
                                        pagination={{
                                            current: page,
                                            pageSize: limit,
                                            total,
                                            onChange: setPage,
                                        }}
                                        scroll={{ x: 'max-content' }}
                                    />
                                )}
                            </Spin>
                        </Card>
                    </Col>
                </Row>
            </div>
        </MainLayout>
    );
};

export default CustomNotifications;
